import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatMoney, formatQuoteDate, formatMonthly, carePlanRate, carePlanIsDiscounted } from '../../utils/quotes';
import { createInvoice, markPaid, deleteInvoice, isOverdue, nextCarePeriod, carePeriodDue, formatDay } from '../../utils/invoices';
import { downloadInvoice } from '../../utils/invoicePdf';
import { CARE_TIER_LABELS } from '../../constants/pricing';
import { MONO } from '../../constants/fonts';
import * as ds from '../../constants/dashboardStyles';

const styles = {
  section: {
    marginTop: 24,
    paddingTop: 20,
    borderTop: '1px solid #ececE6',
  },
  sectionLabel: ds.sectionLabel,
  empty: { ...ds.empty, marginTop: 8 },
  error: ds.error,
  badge: ds.badge,
  listItem: ds.listItem,
  listMain: ds.listMain,
  listMeta: ds.listMeta,
  list: { ...ds.list, marginTop: 10 },
  raiseRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    padding: '10px 12px',
    marginTop: 8,
    background: '#fff',
    border: '1px dashed #c5c5cc',
    borderRadius: 10,
    fontSize: '0.85rem',
  },
  raiseButton: {
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    background: '#111',
    color: '#fff',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
  },
  amount: {
    fontFamily: MONO,
    fontSize: '0.85rem',
    flexShrink: 0,
  },
  smallButton: {
    padding: '5px 10px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#333',
    fontSize: '0.75rem',
    cursor: 'pointer',
    flexShrink: 0,
  },
  removeButton: ds.iconButton,
  stageCount: {
    color: '#999',
    fontSize: '0.75rem',
  },
  noteQuote: {
    fontWeight: 600,
    color: '#777',
  },
  pendingNote: {
    fontSize: '0.78rem',
    color: '#999',
    lineHeight: 1.5,
    margin: '10px 0 0',
  },
};

const BADGE_COLORS = {
  unpaid: { background: '#fef3c7', color: '#92400e' },
  paid: { background: '#d1fae5', color: '#065f46' },
  overdue: { background: '#fee2e2', color: '#991b1b' },
};

// Shown under a selected client in ClientsPanel: which stages of their accepted
// quotes have been billed, and what's been issued.
export default function ClientInvoices({ clientEmail, clientName, quotes, invoices }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const acceptedQuotes = quotes.filter((quote) => quote.status === 'accepted');

  const raise = async (key, payload) => {
    setError('');
    setBusy(key);
    try {
      await createInvoice({ ...payload, clientEmail, clientName }, user.email);
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    } finally {
      setBusy('');
    }
  };

  // A stage is billed if an invoice already points at that quote and index.
  // Enforced here rather than in the rules, which can't cheaply check for the
  // existence of another document.
  const stageBilled = (quoteId, index) => invoices.some(
    (invoice) => invoice.quoteId === quoteId && invoice.stageIndex === index,
  );

  const careInvoicesFor = (quoteId) => invoices.filter(
    (invoice) => invoice.kind === 'care' && invoice.quoteId === quoteId,
  );

  // Invoices arrive newest-first, so the first care one found is the latest.
  const carePeriodFor = (quoteId) => nextCarePeriod(careInvoicesFor(quoteId));

  // Only the next unbilled stage is offered. Stages are a sequence — a deposit
  // then a launch payment — so showing all of them at once both clutters the
  // list and invites billing the final stage before the work has started.
  const nextStage = (quote) => {
    const stages = quote.paymentSchedule || [];
    const index = stages.findIndex((_, i) => !stageBilled(quote.id, i));
    return index === -1 ? null : { index, stage: stages[index] };
  };

  // Care plans run from launch, per our published terms, so they only become
  // billable once every build stage has been invoiced.
  const buildFullyBilled = (quote) => (quote.paymentSchedule || [])
    .every((_, i) => stageBilled(quote.id, i));

  return (
    <div style={styles.section}>
      <div style={styles.sectionLabel}>Invoices</div>
      {error && <div style={styles.error}>{error}</div>}

      {acceptedQuotes.length === 0 && invoices.length === 0 && (
        <p style={styles.empty}>Nothing to invoice yet — a quote has to be accepted first.</p>
      )}

      {acceptedQuotes.map((quote) => {
        const next = nextStage(quote);
        const stagesDone = (quote.paymentSchedule || []).length;
        const carePeriod = quote.carePlan ? carePeriodFor(quote.id) : null;
        // The rate steps back up once the promised number of discounted
        // invoices has been raised — billing follows the wording on its own.
        const careRaised = quote.carePlan ? careInvoicesFor(quote.id).length : 0;
        const careRate = carePlanRate(quote.carePlan, careRaised);
        const careReverted = quote.carePlan
          && carePlanIsDiscounted(quote.carePlan)
          && careRate !== quote.carePlan.monthlyAmount;
        const careReady = quote.carePlan && buildFullyBilled(quote);
        const careDue = careReady && carePeriodDue(carePeriod);
        const careWaiting = careReady && !careDue;
        const carePending = quote.carePlan && !buildFullyBilled(quote);
        if (!next && !careDue && !carePending && !careWaiting) return null;

        return (
          <div key={quote.id}>
            {next && (
              <div style={styles.raiseRow}>
                <span style={styles.listMain}>
                  {quote.title} — {next.stage.label}
                  <span style={styles.stageCount}> · stage {next.index + 1} of {stagesDone}</span>
                </span>
                <span style={styles.amount}>{formatMoney(next.stage.amount)}</span>
                <button
                  type="button"
                  style={styles.raiseButton}
                  disabled={busy === `${quote.id}-${next.index}`}
                  onClick={() => raise(`${quote.id}-${next.index}`, {
                    quoteId: quote.id,
                    quoteTitle: quote.title,
                    kind: 'stage',
                    stageIndex: next.index,
                    description: `${quote.title} — ${next.stage.label} (${next.stage.percent}%)`,
                    amount: next.stage.amount,
                  })}
                >
                  {busy === `${quote.id}-${next.index}` ? 'Raising…' : 'Raise invoice'}
                </button>
              </div>
            )}

            {careDue && (
              <div style={styles.raiseRow}>
                <span style={styles.listMain}>
                  {CARE_TIER_LABELS[quote.carePlan.tier] || quote.carePlan.tier} care plan
                  <span style={styles.stageCount}>
                    {' '}· {formatDay(carePeriod.start)} to {formatDay(carePeriod.end)}
                    {quote.carePlan.discountMonths > 0 && (
                      careReverted
                        ? ' · full rate, discount ended'
                        : ` · discounted month ${careRaised + 1} of ${quote.carePlan.discountMonths}`
                    )}
                  </span>
                </span>
                <span style={styles.amount}>{formatMoney(careRate)}</span>
                <button
                  type="button"
                  style={styles.raiseButton}
                  disabled={busy === `${quote.id}-care`}
                  onClick={() => raise(`${quote.id}-care`, {
                    quoteId: quote.id,
                    quoteTitle: quote.title,
                    kind: 'care',
                    stageIndex: null,
                    period: carePeriod,
                    description: `${CARE_TIER_LABELS[quote.carePlan.tier] || 'Care'} plan — `
                      + `${formatDay(carePeriod.start)} to ${formatDay(carePeriod.end)}`,
                    amount: careRate,
                  })}
                >
                  {busy === `${quote.id}-care` ? 'Raising…' : 'Raise invoice'}
                </button>
              </div>
            )}

            {careWaiting && (
              <p style={styles.pendingNote}>
                {/* Named, because a client with two projects gets two of these
                    and they'd otherwise be indistinguishable. */}
                <span style={styles.noteQuote}>{quote.title}</span> — {' '}
                {CARE_TIER_LABELS[quote.carePlan.tier] || quote.carePlan.tier} care plan, next
                invoice due {formatDay(carePeriod.start)}.
              </p>
            )}

            {carePending && (
              <p style={styles.pendingNote}>
                <span style={styles.noteQuote}>{quote.title}</span> — {' '}
                {CARE_TIER_LABELS[quote.carePlan.tier] || quote.carePlan.tier} care plan
                ({formatMonthly(quote.carePlan.monthlyAmount)}) starts once the build is fully invoiced.
              </p>
            )}
          </div>
        );
      })}

      {invoices.length > 0 && (
        <ul style={styles.list}>
          {invoices.map((invoice) => {
            const overdue = isOverdue(invoice);
            const state = invoice.status === 'paid' ? 'paid' : overdue ? 'overdue' : 'unpaid';
            return (
              <li key={invoice.id} style={styles.listItem}>
                <div style={styles.listMain}>
                  <div>{invoice.reference} · {invoice.description}</div>
                  <div style={styles.listMeta}>
                    {formatMoney(invoice.amount)}
                    {invoice.status === 'paid'
                      ? ` · paid ${formatQuoteDate(invoice.paidAt) || ''}`
                      : ` · due ${formatQuoteDate(invoice.dueAt) || ''}`}
                  </div>
                </div>
                <span style={{ ...styles.badge, ...BADGE_COLORS[state] }}>
                  {state === 'overdue' ? 'Overdue' : state === 'paid' ? 'Paid' : 'Unpaid'}
                </span>
                <button
                  type="button"
                  style={styles.smallButton}
                  onClick={() => markPaid(invoice.id, invoice.status !== 'paid').catch((e) => setError(e.message))}
                >
                  {invoice.status === 'paid' ? 'Mark unpaid' : 'Mark paid'}
                </button>
                <button
                  type="button"
                  style={styles.smallButton}
                  onClick={() => downloadInvoice(invoice)}
                  title="Download PDF"
                >
                  ⤓
                </button>
                {invoice.status !== 'paid' && (
                  <button
                    type="button"
                    style={styles.removeButton}
                    onClick={() => deleteInvoice(invoice.id).catch((e) => setError(e.message))}
                    title="Delete — only for one raised in error"
                    aria-label={`Delete ${invoice.reference}`}
                  >
                    &times;
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
